import { isEmpty } from 'lodash-es';
import React, {
  createContext, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useAchievementsContext } from '../../../../contexts/AchievementsContext/AchievementsContext';
import { deepDiff } from '../../../utils/deepDiff';
import { IAchievementModal } from '../AchievementsModalTypes';
import { useEpisodes } from '../hooks/useEpisodes';
import { AchievementsModalContextProviderProps, AchievementsModalContextType } from './AchievementsModalContextTypes';

export const AchievementsModalContext = createContext<AchievementsModalContextType | undefined>(undefined);

export function AchievementsModalContextProvider(props: AchievementsModalContextProviderProps) {
  const {
    children, achievements: initialAchievements, onHide, readOnly = false,
  } = props;
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [achievements, setAchievements] = useState<IAchievementModal[]>(initialAchievements);
  const [uiVersion, setUiVersion] = useState(0);

  const { bookId } = useAchievementsContext();
  const { episodes, isLoading: isEpisodesLoading } = useEpisodes(bookId);

  useEffect(() => {
    setIsFormLoading(isEpisodesLoading);
  }, [isEpisodesLoading]);

  useEffect(() => {
    setAchievements(initialAchievements);
  }, [initialAchievements]);

  const hasValueChanged = useCallback((value: IAchievementModal) => {
    const initialAchievement = achievements.find((a) => a.id === value.id);
    const hasChanged = value.isAdded || !isEmpty(deepDiff(initialAchievement, value));
    return hasChanged;
  }, [achievements]);

  const haveValuesChanged = useCallback((values: IAchievementModal[]) => values.some((value) => hasValueChanged(value)), [hasValueChanged]);

  const refreshUi = useCallback(() => {
    setUiVersion((k) => k + 1);
  }, []);

  const value: AchievementsModalContextType = useMemo(() => ({
    isFormLoading,
    setIsFormLoading,
    onHide,
    achievements,
    episodes,
    setAchievements,
    hasValueChanged,
    haveValuesChanged,
    refreshUi,
    uiVersion,
    readOnly,
  }), [achievements, episodes, hasValueChanged, haveValuesChanged, isFormLoading, onHide, readOnly, refreshUi, uiVersion]);

  return (
    <AchievementsModalContext.Provider value={value}>
      {children}
    </AchievementsModalContext.Provider>
  );
}

export const useAchievementsModalContext = () => {
  const context = React.useContext(AchievementsModalContext);
  if (context === undefined) {
    throw new Error('useAchievementsModalContext must be used within a AchievementsModalContextProvider');
  }
  return context;
};
